import { Injectable, signal } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import { SessionStateService } from '../auth/session-state.service'
import { ApiShortcutPreferences } from '../contracts/shared/keyboard-shortcut.endpoints'
import { KeyboardShortcutApiService } from '../services/shared/keyboard-shortcut-api.service'
import { DEFAULT_PREFERENCES, ShortcutPreferences, UserActionType } from './shortcut.types'

export interface SaveResult {
    ok: boolean
    error?: string
}

/**
 * The signed-in user's shortcut preferences (bindings + disable rules), stored
 * per user by the API (`:panel/keyboard-shortcuts/me`).
 *
 * A localStorage copy per user makes them apply instantly on reload and keeps
 * other tabs in sync; the API copy is the source of truth and is fetched on
 * every login / app start.
 */
@Injectable({ providedIn: 'root' })
export class ShortcutPreferencesService {
    private readonly prefsSig = signal<ShortcutPreferences>(DEFAULT_PREFERENCES)

    readonly prefs = this.prefsSig.asReadonly()

    private userId: number | string | null = null

    private panel: string | null = null

    constructor (session: SessionStateService, private api: KeyboardShortcutApiService) {
        session.userLoggedIn$.subscribe(loggedIn => {
            const user = loggedIn ? session.user : null
            this.userId = user?.id ?? null
            this.panel = user?.userable_type === 'workshop' || user?.userable_type === 'customer' ? user.userable_type : null
            this.prefsSig.set(this.readCache())
            this.refresh()
        })

        // Another tab saved: pick up its copy
        window.addEventListener('storage', e => {
            if (e.key && e.key === this.cacheKey) this.prefsSig.set(this.readCache())
        })
    }

    /** Re-fetches from the API (no-op outside the workshop/customer panels). */
    refresh () {
        const panel = this.panel
        if (!panel) return
        this.api.get(panel).subscribe({
            next: resp => {
                if (resp.success && panel === this.panel) this.apply(fromApi(resp.data))
            },
            error: () => { /* offline / session ended: keep the cached copy */ }
        })
    }

    /** Saves the whole document. Applied optimistically, reverted if the API rejects it. */
    async save (next: ShortcutPreferences): Promise<SaveResult> {
        const previous = this.prefsSig()
        this.apply(next)
        if (!this.panel) return { ok: true }

        try {
            const resp = await firstValueFrom(this.api.save(this.panel, toApi(next)))
            if (resp.success) {
                this.apply(fromApi(resp.data))
                return { ok: true }
            }
            this.apply(previous)
            return { ok: false, error: firstError(resp) }
        }
        catch {
            this.apply(previous)
            return { ok: false }
        }
    }

    async reset (): Promise<SaveResult> {
        const previous = this.prefsSig()
        this.apply(DEFAULT_PREFERENCES)
        if (!this.panel) return { ok: true }

        try {
            const resp = await firstValueFrom(this.api.reset(this.panel))
            if (resp.success) return { ok: true }
            this.apply(previous)
            return { ok: false, error: firstError(resp) }
        }
        catch {
            this.apply(previous)
            return { ok: false }
        }
    }

    private apply (prefs: ShortcutPreferences) {
        this.prefsSig.set(prefs)
        this.writeCache(prefs)
    }

    private get cacheKey (): string | null {
        return this.userId === null ? null : `keyboard_shortcuts_${this.userId}`
    }

    private readCache (): ShortcutPreferences {
        const key = this.cacheKey
        if (!key) return DEFAULT_PREFERENCES
        try {
            const raw = localStorage.getItem(key)
            if (!raw) return DEFAULT_PREFERENCES
            const parsed = JSON.parse(raw) as Partial<ShortcutPreferences>
            return {
                settings: { ...DEFAULT_PREFERENCES.settings, ...parsed.settings },
                bindings: Array.isArray(parsed.bindings) ? parsed.bindings : []
            }
        }
        catch {
            return DEFAULT_PREFERENCES
        }
    }

    private writeCache (prefs: ShortcutPreferences) {
        const key = this.cacheKey
        if (!key) return
        try {
            localStorage.setItem(key, JSON.stringify(prefs))
        }
        catch { /* storage full or blocked: prefs still apply for this session */ }
    }
}

function fromApi (data: ApiShortcutPreferences): ShortcutPreferences {
    return {
        settings: {
            enabled: data.settings?.enabled ?? true,
            rules: data.settings?.rules ?? []
        },
        bindings: (data.bindings ?? []).map(b => ({
            id: b.id,
            scope: b.scope,
            keys: b.keys,
            commandId: b.command_id ?? null,
            action: b.action_type ? { type: b.action_type as UserActionType, payload: b.action_payload ?? {} } : null,
            label: b.label ?? null
        }))
    }
}

function toApi (prefs: ShortcutPreferences): ApiShortcutPreferences {
    return {
        settings: prefs.settings,
        bindings: prefs.bindings.map(b => ({
            scope: b.scope,
            keys: b.keys,
            command_id: b.commandId || null,
            action_type: b.commandId ? null : b.action?.type ?? null,
            action_payload: b.commandId ? null : b.action?.payload ?? null,
            label: b.label || null
        }))
    }
}

function firstError (resp: { errors?: unknown, msg?: string }): string | undefined {
    const errors = resp.errors as Record<string, unknown> | undefined
    const general = errors?.['general']
    return typeof general === 'string' ? general : resp.msg
}
